/**
 * InstagramAdMockup Component
 *
 * Instagram Feed ad mockup
 * Square/portrait format with bottom CTA
 */

import React from 'react';
import { stripMediaTags } from '@/utils/mediaTagParser';
import { AdMockupContainer } from '../base/AdMockupContainer';
import { AdMockupMedia } from '../base/AdMockupMedia';
import { AdMockupCTA } from '../base/AdMockupCTA';
import { AdMockupEngagement } from '../base/AdMockupEngagement';
import { InstagramAdMockupProps } from '../types/platform.types';

export const InstagramAdMockup: React.FC<InstagramAdMockupProps> = ({
  imageUrl,
  caption,
  captions,
  callToAction = 'Learn More',
  brandName = 'your_brand',
  brandLogoUrl,
  hashtags = [],
}) => {
  // Support both singular and plural props for backward compatibility
  const allCaptions = captions || (caption ? [caption] : []);
  // Strip MEDIA tags from caption before display (image shown separately above)
  const displayCaption = stripMediaTags(allCaptions[0] || '');

  return (
    <AdMockupContainer platform="instagram">
      <div className="bg-white dark:bg-background-dark rounded-lg border border-gray-200 dark:border-border-dark overflow-hidden shadow-sm max-w-md mx-auto">

        {/* Post Header with gradient ring */}
        <div className="flex items-center gap-3 p-3">
          <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-600 via-pink-600 to-orange-500 p-0.5">
            <div className="w-full h-full rounded-full bg-white dark:bg-background-dark flex items-center justify-center">
              {brandLogoUrl ? (
                <img
                  src={brandLogoUrl}
                  alt={brandName}
                  className="w-7 h-7 rounded-full object-cover"
                />
              ) : (
                <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary to-blue-600 flex items-center justify-center text-white text-xs font-bold">
                  {brandName.slice(0, 2).toUpperCase()}
                </div>
              )}
            </div>
          </div>
          <div className="flex-1">
            <p className="font-semibold text-sm text-gray-900 dark:text-white">
              {brandName}
            </p>
            <p className="text-xs text-gray-500 dark:text-gray-400">Sponsored</p>
          </div>
          <button className="text-gray-400">
            <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path d="M10 6a2 2 0 110-4 2 2 0 010 4zM10 12a2 2 0 110-4 2 2 0 010 4zM10 18a2 2 0 110-4 2 2 0 010 4z" />
            </svg>
          </button>
        </div>

        {/* Image (Square aspect ratio) */}
        {imageUrl && (
          <AdMockupMedia
            mediaUrl={imageUrl}
            aspectRatio="1:1"
            platform="instagram"
            alt="Instagram ad"
          />
        )}

        {/* Engagement Icons */}
        <AdMockupEngagement platform="instagram" showCounts={false} />

        {/* Caption with CTA */}
        <div className="px-3 pb-3">
          <p className="text-sm text-gray-900 dark:text-white mb-2">
            <span className="font-semibold">{brandName}</span> {displayCaption}
            {hashtags.length > 0 && (
              <span className="text-primary">
                {' '}{hashtags.map(tag => `#${tag}`).join(' ')}
              </span>
            )}
          </p>

          {/* Show additional caption variations */}
          {allCaptions.length > 1 && (
            <div className="mb-3 pt-2 border-t border-gray-200 dark:border-border-dark">
              <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 mb-2">
                📝 {allCaptions.length - 1} More Caption{allCaptions.length > 2 ? 's' : ''}:
              </p>
              {allCaptions.slice(1, 3).map((cap, idx) => (
                <p key={idx} className="text-xs text-gray-600 dark:text-gray-400 mb-1 pl-2 border-l-2 border-purple-300 dark:border-purple-600">
                  {cap.length > 80 ? `${cap.slice(0, 80)}...` : cap}
                </p>
              ))}
              {allCaptions.length > 3 && (
                <p className="text-xs text-gray-500 dark:text-gray-400 italic">
                  +{allCaptions.length - 3} more...
                </p>
              )}
            </div>
          )}

          <AdMockupCTA
            text={callToAction}
            platform="instagram"
            variant="primary"
            fullWidth={true}
          />
        </div>
      </div>
    </AdMockupContainer>
  );
};

export default InstagramAdMockup;
